/**
 * Media Gallery — frontend bootstrap (PhotoSwipe lightbox + Embla slider).
 *
 * Lazy-loads PhotoSwipe v5 only when a gallery with lightbox enabled exists
 * on the page. PhotoSwipe core is further deferred until first user click via
 * the `pswpModule` callback. End result: 0 KB on pages without the block.
 *
 * Slider mode reuses the globally-enqueued `brandy-embla-script`
 * (window.EmblaCarousel) — no extra bundle. Init waits for the global
 * because `viewScriptModule` may load before the classic Embla script.
 *
 * @package BrandyBlocks
 */

const LIGHTBOX_SELECTOR = '[data-brandy-mediagallery][data-lightbox-enabled="true"]';
const SLIDER_SELECTOR = '[data-brandy-mediagallery][data-display-mode="slider"]';

/**
 * HTML-entity encode untrusted text before passing to the caption plugin
 * (which uses innerHTML internally). Forces text rendering, no HTML parse.
 */
function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

async function initLightbox(galleryEl) {
  // PhotoSwipe + caption-plugin JS, plus their CSS — all dynamically imported
  // so they only load on first lightbox interaction.
  const [{ default: PhotoSwipeLightbox }, { default: PhotoSwipeDynamicCaption }] = await Promise.all([
    import('photoswipe/lightbox'),
    import('photoswipe-dynamic-caption-plugin'),
    import('photoswipe/style.css'),
    import('photoswipe-dynamic-caption-plugin/photoswipe-dynamic-caption-plugin.css'),
  ]);

  const loop = galleryEl.dataset.lightboxLoop === '1';
  const showCounter = galleryEl.dataset.lightboxCounter === '1';

  const lightbox = new PhotoSwipeLightbox({
    gallery: galleryEl,
    children: '[data-pswp-item]',
    pswpModule: () => import('photoswipe'),
    loop,
    initialZoomLevel: 'fit',
    secondaryZoomLevel: 1.5,
    maxZoomLevel: 3,
    allowPanToNext: true,
    // Reserve viewport space so PhotoSwipe's image-fit math leaves room for
    // our fixed-position caption (~80px) and a top breathing margin.
    padding: { top: 30, bottom: 80, left: 16, right: 16 },
  });

  // Map each gallery child to a slide. Reads the data-pswp-* attributes the
  // server emitted in MediaGalleryItem/render.php (Phase 03).
  lightbox.addFilter('itemData', (itemData) => {
    const el = itemData.element;
    if (!el) return itemData;
    return {
      ...itemData,
      src: el.dataset.pswpSrc,
      width: parseInt(el.dataset.pswpW, 10) || 0,
      height: parseInt(el.dataset.pswpH, 10) || 0,
      alt: el.dataset.pswpAlt || '',
      // Caption plugin reads `slide.data.title`; fall back to alt.
      title: el.dataset.pswpCaption || el.dataset.pswpAlt || '',
    };
  });

  // Images inserted by link have no attachment metadata, so render.php emitted
  // zero dimensions and PhotoSwipe would fall back to viewport size with zoom
  // disabled. Read the real size off the loaded <img> and re-lay-out the slide.
  lightbox.on('loadComplete', ({ slide, content }) => {
    if (!slide || slide.width) return;
    const { naturalWidth, naturalHeight } = content?.element ?? {};
    if (!naturalWidth || !naturalHeight) return;

    content.width = naturalWidth;
    content.height = naturalHeight;
    slide.width = naturalWidth;
    slide.height = naturalHeight;

    // Slides not yet appended pick the corrected dimensions up in append();
    // only mounted ones need an explicit re-layout.
    if (!slide.holderElement) return;
    slide.calculateSize();
    slide.currentResolution = 0;
    slide.zoomAndPanToInitial();
    slide.applyCurrentZoomPan();
    slide.updateContentSize(true);
  });

  // Caption — official plugin, themed via .pswp__dynamic-caption in style.scss.
  // The plugin assigns our return value via innerHTML, so we must escape HTML
  // entities to prevent XSS from author-controlled heading/paragraph text.
  // type: 'below' keeps the caption directly under the image regardless of
  // aspect ratio (the 'auto' default flips to side/aside for tall images).
  // eslint-disable-next-line no-new
  new PhotoSwipeDynamicCaption(lightbox, {
    type: 'below',
    captionContent: (slide) => escapeHtml(slide.data.title || ''),
  });

  // Counter visibility — toggle via class so CSS in style.scss can hide it.
  // `uiRegister` fires inside _createMainStructure() AFTER pswp.element is
  // assigned; `beforeOpen` is too early (element undefined) and the optional
  // chain would silently no-op.
  if (!showCounter) {
    lightbox.on('uiRegister', () => {
      lightbox.pswp.element.classList.add('pswp--no-counter');
    });
  }

  lightbox.init();
}

/**
 * Wait for the globally-enqueued brandy-embla-script to define EmblaCarousel.
 * Module scripts can run before classic deferred scripts; poll briefly.
 */
async function waitForEmbla(timeoutMs = 2000) {
  const start = Date.now();
  while (typeof window.EmblaCarousel !== 'function') {
    if (Date.now() - start > timeoutMs) {
      // eslint-disable-next-line no-console
      console.warn('[brandy/media-gallery] EmblaCarousel global not found within timeout');
      return null;
    }
    await new Promise((r) => setTimeout(r, 50));
  }
  return window.EmblaCarousel;
}

/**
 * Wait for the controls registry global (brandy-embla-registry, a classic
 * deferred script) to define window.brandyEmbla. Returns null on timeout.
 */
async function waitForBrandyEmbla(timeoutMs = 2000) {
  const start = Date.now();
  while (!window.brandyEmbla) {
    if (Date.now() - start > timeoutMs) {
      // eslint-disable-next-line no-console
      console.warn('[brandy/media-gallery] brandyEmbla registry not found within timeout');
      return null;
    }
    await new Promise((r) => setTimeout(r, 50));
  }
  return window.brandyEmbla;
}

async function initSlider(galleryEl) {
  const Embla = await waitForEmbla();
  if (!Embla) return;

  const viewportNode = galleryEl.querySelector('.embla__viewport');
  if (!viewportNode) return;

  const emblaNode = galleryEl.querySelector('.embla');
  const loop = emblaNode?.dataset.loop !== 'false';
  const api = Embla(viewportNode, { loop, align: 'start' });

  // Nav + dots come from the shared controls registry (window.brandyEmbla),
  // resolved from data-nav-style / data-dots-style with a default fallback —
  // same path as embla-extra.js, so themes' variants work here too.
  const be = await waitForBrandyEmbla();
  if (be && emblaNode) {
    const navFn = be.nav.resolve(emblaNode.getAttribute('data-nav-style') || 'default');
    if (navFn) {
      const cleanup = navFn(api, emblaNode);
      if (typeof cleanup === 'function') api.on('destroy', cleanup);
    }
    const dotsFn = be.dots.resolve(emblaNode.getAttribute('data-dots-style') || 'default');
    if (dotsFn) {
      const cleanup = dotsFn(api, emblaNode);
      if (typeof cleanup === 'function') api.on('destroy', cleanup);
    }
  }
}

function bootstrap() {
  const lightboxGalleries = document.querySelectorAll(LIGHTBOX_SELECTOR);
  lightboxGalleries.forEach((g) => {
    // Mark the gallery as lightbox-active so CSS can scope cursor + a11y hints
    // only to galleries that actually open the lightbox.
    g.classList.add('has-lightbox');
    initLightbox(g).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('[brandy/media-gallery] PhotoSwipe init failed', err);
    });
  });

  const sliderGalleries = document.querySelectorAll(SLIDER_SELECTOR);
  sliderGalleries.forEach((g) => {
    initSlider(g).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('[brandy/media-gallery] Embla init failed', err);
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
} else {
  bootstrap();
}
